import { useState, useMemo, useCallback, useEffect, memo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  IconButton,
  Collapse,
  Box,
  TableSortLabel,
  Table,
  TableCell,
  Chip,
  Typography,
  Stack,
  TableRow,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { sortTypes, stableSort } from './sorting';
import { DynamicComponent } from './dynamic-component';
import { TableVirtuoso } from 'react-virtuoso';
import {
  colors,
  CompactTableCell,
  CompactTableRow,
  GridContainer,
  GridItem,
} from './ui-extension';
import { LabeledInput } from './labeled-input';
import FaIcon from './icon';
import {
  rowOriginalFields,
  getComparatorByType,
  transformRowsByColumns,
} from './table-helpers';
import Loader from './loader';
import { useNotificationContext } from './notification';
import { printRoundedNumber, removeEmptyValues } from '../../shared/utils';
import { directions } from '../../constants/others';

const Row = memo(
  ({
    expandRow,
    row,
    rowExpansion,
    setRowExpansion,
    columns,
    expandIconLocation,
    backgroundColor,
    alwaysExpandRows,
    onRowClick,
    showCursorPointer,
  }) => {
    const isChildRow = !!row.parentRowId;
    const isRowExpanded =
      alwaysExpandRows ||
      !!rowExpansion[isChildRow ? row.parentRowId : row.rowId];

    const renderContent = (content) => {
      if (typeof content === 'function') {
        return <DynamicComponent component={content} />;
      }
      return <Typography variant='body2'>{content}</Typography>;
    };

    const renderData = (row) => {
      return (
        <>
          {/** todo , add expandIconLocation field specific (provide field key) */}
          {(expandRow && expandIconLocation === directions.left) ||
          (!expandIconLocation && expandRow) ? (
            <CompactTableCell variant='body'>
              <IconButton
                aria-label='expand row'
                size='small'
                onClick={() => {
                  setRowExpansion((prev) => ({
                    ...prev,
                    [row.rowId]: !isRowExpanded,
                  }));
                  row?.expandableComponent?.props?.onExpandClick?.(
                    row,
                    isRowExpanded
                  ) || {};
                }}
                sx={{
                  backgroundColor,
                  padding: '3px 5px',
                }}
              >
                {isRowExpanded ? (
                  <KeyboardArrowUpIcon />
                ) : (
                  <KeyboardArrowDownIcon />
                )}
              </IconButton>
            </CompactTableCell>
          ) : (
            false
          )}
          {Object.keys(row).map((key, colIndex) => {
            return ![
              'expandableComponent',
              'rowId',
              rowOriginalFields,
            ].includes(key) ? (
              <CompactTableCell
                variant='body'
                onClick={() => onRowClick(row)}
                key={colIndex}
                sx={(() => {
                  let cellSx = columns?.find((c) => c.id === key)?.cellSx;
                  if (typeof cellSx === 'function') {
                    cellSx = cellSx(row[rowOriginalFields]);
                  }
                  return {
                    backgroundColor,
                    ...(cellSx || {}),
                    ...(showCursorPointer ? { cursor: 'pointer' } : {}),
                  };
                })()}
              >
                {renderContent(row[key])}
              </CompactTableCell>
            ) : null;
          })}
          {!alwaysExpandRows &&
          expandRow &&
          expandIconLocation === directions.right ? (
            <CompactTableCell variant='body'>
              <IconButton
                aria-label='expand row'
                size='small'
                onClick={() => {
                  setRowExpansion((prev) => ({
                    ...prev,
                    [row.rowId]: !isRowExpanded,
                  }));
                  row?.expandableComponent?.props?.onExpandClick?.(
                    row,
                    isRowExpanded
                  ) || {};
                  onRowClick(row);
                }}
                sx={{
                  backgroundColor: '#ffffff',
                  padding: '3px 5px',
                }}
              >
                {row?.expandableComponent?.label ? (
                  <Typography variant='caption' sx={{ marginRight: '5px' }}>
                    {row.expandableComponent.label}
                  </Typography>
                ) : null}
                {isRowExpanded ? (
                  <KeyboardArrowUpIcon />
                ) : (
                  <KeyboardArrowDownIcon />
                )}
              </IconButton>
            </CompactTableCell>
          ) : (
            false
          )}
        </>
      );
    };

    const expandableRow = (row) => {
      return (
        <>
          {expandRow && isRowExpanded ? (
            <CompactTableCell
              variant='body'
              colSpan={10}
              style={{
                padding: '0px',
                backgroundColor: colors.lightGray3,
              }}
            >
              <Collapse in={isRowExpanded} timeout='auto' unmountOnExit>
                {row.expandableComponent.header &&
                  renderContent(row.expandableComponent.header)}
                <DynamicComponent
                  component={row.expandableComponent.component}
                  props={row.expandableComponent.props}
                />
              </Collapse>
            </CompactTableCell>
          ) : (
            <TableCell
              variant='head'
              sx={{
                border: 'none',
                height: '1px',
                padding: '0px',
                margin: '0px',
              }}
            />
          )}
        </>
      );
    };

    return isChildRow ? expandableRow(row) : renderData(row);
  }
);

const Filter = ({
  fieldName,
  updateChange,
  initialValue,
  variant = 'standard',
  placeholder,
  label,
  sx,
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleChange = (event) => {
    const newValue = event.target.value;
    setValue(newValue);
  };

  return (
    <LabeledInput
      label={label}
      placeholder={placeholder}
      value={value}
      variant={variant}
      onChange={handleChange}
      sx={{ minHeight: label ? '48px' : '36px', ...sx }}
      onBlur={() => updateChange({ [fieldName]: value })}
    />
  );
};

/**
 * TableWrapper component renders a table with various functionalities such as sorting, filtering, searching, and infinite scrolling.
 *
 * @param {Array} props.rows - The rows to be displayed in the table.
 * @param {Array} [props.columns=[]] - The columns configuration for the table.
 * @param {boolean} [props.transformRows=false] - Flag to indicate if rows should be transformed.
 * @param {Object} [props.style={}] - Custom styles for the table.
 * @param {Function} [props.expandRow] - Function to handle row expansion.
 * @param {string} [props.orderBy] - The column id to order by.
 * @param {string} [props.orderDirection=sortTypes.asc] - The direction of sorting (asc or desc).
 * @param {boolean} [props.showSearch=false] - Flag to show search input.
 * @param {string} [props.placeholder] - Placeholder text for the search input.
 * @param {boolean} [props.showFilter=false] - Flag to show filter options.
 * @param {boolean} [props.showLoader=false] - Flag to show loader while fetching rows.
 * @param {Function} [props.fetchRows] - Function to fetch rows from an API.
 * @param {Object} [props.fetchQueries] - Additional queries for fetching rows.
 * @param {Function} [props.setFetchQueries] - Function to set additional queries for fetching rows.
 * @param {Function} [props.onRowsFetched] - Callback function to be called after rows are fetched.
 * @param {Function} [props.filterVisibleRows] - Function to filter visible rows.
 * @param {number} [props.rowsPerPage=100] - Number of rows to fetch per page.
 * @param {string} [props.tableType='items'] - Type of the table (e.g., items, users).
 * @param {Function} [props.renderAdditionalComponents] - Function to render additional components.
 * @param {Object} [props.gridStyle] - Additional props to spread onto the wrapping Grid container.
 *
 * @returns {JSX.Element} The rendered TableWrapper component.
 */
const TableWrapper = ({
  rows,
  columns = [],
  transformRows = false,
  style = {},
  expandRow,
  orderBy,
  orderDirection = sortTypes.asc,
  showSearch = false,
  placeholder,
  showFilter = false,
  showLoader = false,
  fetchRows,
  fetchQueries,
  setFetchQueries,
  onRowsFetched,
  filterVisibleRows,
  rowsPerPage = 100,
  tableType = 'items',
  renderAdditionalComponents,
  gridStyle,
  expandIconLocation = directions.left,
  alternateRowColors = false,
  alwaysExpandRows = false,
  showCursorPointer = false,
  onRowClick = () => {},
  useVirtualTable = true,
  searchExpandableRows = false,
}) => {
  const { useExceptionHandler } = useNotificationContext();

  const [order, setOrder] = useState(orderDirection);
  const [orderByCol, setOrderByCol] = useState(orderBy);
  const [visibleRows, setVisibleRows] = useState([]);
  const [fetchedRows, setFetchedRows] = useState([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [rowExpansion, setRowExpansion] = useState({});
  const [localSearchString, setLocalSearchString] = useState('');
  const [filterByColumns, setFilterByColumns] = useState({});
  const [isFetchingRows, setIsFetchingRows] = useState(false);

  const allRows = fetchRows ? fetchedRows : rows;

  const timeoutRef = useRef();

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      fetchRowsFromApi();
    }, 250);

    return () => clearTimeout(timeoutRef.current);
  }, [fetchQueries]);

  const fetchIndex = useRef(0);

  useEffect(() => {
    fetchIndex.current = 0;
  }, []);

  const endOfRowsReached = fetchedRows?.length === totalRowCount;

  const fetchRowsFromApi = useExceptionHandler(
    async ({ page = 1, fetchCriteriaChanged = true } = {}) => {
      if (!fetchRows) return;

      if (fetchCriteriaChanged) {
        setFetchedRows([]);
      } else if (endOfRowsReached) {
        return;
      } else if (fetchedRows.length >= rowsPerPage * page) {
        // Do not fetch rows that are already fetched.
        return;
      }

      setIsFetchingRows(true);

      fetchIndex.current += 1;
      const currentApiCallCount = fetchIndex.current;

      const { data } = await fetchRows({
        page,
        rowsPerPage,
        ...fetchQueries,
      });

      // We should only display the latest API response requested by the user. A
      // previously requested API response should not override the latest one.
      if (currentApiCallCount < fetchIndex.current) return;

      let newFetchedRows = [];

      if (data.rows) {
        newFetchedRows = data.rows;
        setTotalRowCount(data.totalRowCount || 0);
      } else {
        newFetchedRows = data;
        setTotalRowCount(data.length || 0);
      }

      const indexedRows = newFetchedRows.map((d, i) => ({
        renderIndex: (page - 1) * rowsPerPage + i,
        ...d,
      }));

      setFetchedRows((prev) => {
        const updatedRows = fetchCriteriaChanged ? [] : [...prev];

        indexedRows.forEach((d) => {
          if (d.renderIndex >= 0) {
            updatedRows[d.renderIndex] = d;
          }
        });

        // Remove potential gaps in array.
        return updatedRows.filter((r) => !!r);
      });
    },
    () => {
      // Avoid memory leak in case the component is unmounted.
      if (fetchIndex.current) setIsFetchingRows(false);
    }
  );

  useEffect(() => {
    resetLocalFiltersAndOrders();
    updateRowData();
  }, [columns, allRows]);

  useEffect(() => {
    if (allRows && allRows?.length) {
      resetLocalFiltersAndOrders();
      updateRowData(filterVisibleRows);
    }
  }, [filterVisibleRows]);

  useEffect(() => {
    onRowsFetched?.({
      fetchedRows: [...fetchedRows],
      isEndOfRowReached: fetchedRows.length === totalRowCount,
      totalRowCount,
    });
  }, [fetchedRows]);

  const updateRow = useExceptionHandler(
    async ({ getUpdatedRows, rowsMatch }) => {
      if (!getUpdatedRows || !rowsMatch) return;

      const updatedRows = await getUpdatedRows(fetchedRows);

      if (!updatedRows?.length) return;

      setFetchedRows((prev) => [
        ...prev.map((p) => updatedRows.find((pr) => rowsMatch?.(pr, p)) || p),
      ]);
    }
  );

  const updateRowData = (filter) => {
    const transformedRows = transformRows
      ? transformRowsByColumns(columns, allRows, updateRow)
      : allRows;

    const filteredRows = filter?.(transformedRows) || transformedRows;

    setVisibleRows(filteredRows);
  };

  const resetLocalFiltersAndOrders = () => {
    setLocalSearchString('');
    setFilterByColumns({});
  };

  const handleSort = (columnId) => {
    setOrderByCol(columnId);
    const flipSort = orderByCol === columnId;

    if (flipSort) {
      setOrder(order === sortTypes.asc ? sortTypes.desc : sortTypes.asc);
    } else {
      setOrder(sortTypes.desc);
    }
  };

  const sortedRows = useMemo(
    () =>
      stableSort(
        visibleRows,
        getComparatorByType({ columns, orderByCol, order })
      ),
    [visibleRows, order, orderByCol]
  );

  const virtualRows = useMemo(() => {
    return sortedRows.reduce((acc, row, i) => {
      const id = row.__rowOriginalFields?.rowId || i + 1;
      acc.push({ ...row, rowId: id });
      if (row.autoExpand) {
        setRowExpansion((prev) => ({ ...prev, [id]: true }));
      }
      if (row.expandableComponent) {
        acc.push({
          expandableComponent: row.expandableComponent,
          parentRowId: id,
        });
      }
      return acc;
    }, []);
  }, [sortedRows, expandRow, localSearchString]);

  Row.displayName = 'Row';

  const generateRow = useCallback(
    (index, row) => (
      <Row
        key={index}
        backgroundColor={
          alternateRowColors && index % 2 === 1
            ? colors.lightGray3
            : colors.white
        }
        row={row}
        expandRow={expandRow}
        rowExpansion={rowExpansion}
        setRowExpansion={setRowExpansion}
        columns={columns}
        expandIconLocation={expandIconLocation}
        alwaysExpandRows={alwaysExpandRows}
        onRowClick={onRowClick}
        showCursorPointer={showCursorPointer}
      />
    ),
    [expandRow, rowExpansion, columns]
  );

  const fixedHeaderContent = useCallback(() => {
    const allRowsFetched = !fetchRows || endOfRowsReached;

    return (
      <CompactTableRow
        sx={{
          textTransform: 'capitalize',
        }}
      >
        {expandRow && expandIconLocation === directions.left && (
          <CompactTableCell variant='head' />
        )}
        {columns.map((column) => {
          return (
            <CompactTableCell
              sx={{
                padding: '0 5px',
                ...(typeof column.cellSx === 'function'
                  ? column.cellSx({})
                  : column.cellSx),
              }}
              key={column.id}
              padding={'normal'}
              variant='head'
              sortDirection={orderByCol === column.id ? order : false}
            >
              {/* Not sortable unless all rows are loaded */}
              {column.isSortable && allRowsFetched ? (
                <TableSortLabel
                  active={orderByCol === column.id}
                  direction={orderByCol === column.id ? order : sortTypes.asc}
                  onClick={() => handleSort(column.id)}
                  sx={{
                    margin: 0,
                    padding: 0,
                    minWidth: 85,
                  }}
                >
                  {column.label}
                </TableSortLabel>
              ) : (
                column.label
              )}
              {column.label && showFilter && allRowsFetched ? (
                <Filter
                  key={column.id + '_filter'}
                  fieldName={column.id}
                  initialValue={filterByColumns[column.id] || ''}
                  placeholder={'Filter'}
                  updateChange={handleSearchAndFilter}
                  sx={{
                    padding: '2px 0',
                  }}
                />
              ) : (
                false
              )}
            </CompactTableCell>
          );
        })}
        {expandRow && expandIconLocation === directions.right && (
          <CompactTableCell variant='head' />
        )}
      </CompactTableRow>
    );
  }, [columns, expandRow, handleSort, order, orderByCol, filterByColumns]);

  const fetchRowSpinner = useMemo(() => {
    return isFetchingRows ? (
      <CompactTableRow>
        <CompactTableCell
          colSpan={columns.length + 1}
          sx={{
            textAlign: 'center',
            backgroundColor: 'rgb(0, 0, 0, 0.05) ',
          }}
        >
          <Loader />
        </CompactTableCell>
      </CompactTableRow>
    ) : (
      false
    );
  }, [isFetchingRows]);

  const fixedFooterContent = () => fetchRowSpinner;

  // Recursively search for a value in the props of a component
  const searchPropsForValue = (props, value) => {
    if (!props || !value) {
      return false;
    }

    if (typeof props === 'string') {
      return props.toLowerCase().includes(value);
    }

    if (typeof props === 'number' || typeof props === 'boolean') {
      return String(props).toLowerCase().includes(value);
    }

    if (Array.isArray(props)) {
      return props.some((item) => searchPropsForValue(item, value));
    }

    if (typeof props === 'object') {
      if (props.props) {
        return searchPropsForValue(props.props, value);
      }

      if (props.children) {
        return searchPropsForValue(props.children, value);
      }

      if (props.rows) {
        return searchPropsForValue(props.rows, value);
      }

      return Object.values(props).some((v) => searchPropsForValue(v, value));
    }

    return false;
  };

  const doesRowMatch = (row, columnId, queries = []) => {
    const columnsToIgnore = [
      ...(searchExpandableRows ? [] : ['expandableComponent']),
      'rowId',
      'actionButtons',
      'autoExpand',
      rowOriginalFields,
    ];

    if (columnsToIgnore.includes(columnId)) return false;

    return queries
      .map((q) => q?.trim().toLowerCase())
      .filter((q) => q)
      .some((query) => {
        if (!query) return false;

        let columnValue = row[columnId];

        if (typeof columnValue === 'function') {
          columnValue = columnValue();
        }

        if (typeof columnValue === 'object' && columnValue?.props) {
          return searchPropsForValue(columnValue.props, query);
        }

        return String(columnValue || '')
          .toLowerCase()
          .includes(query);
      });
  };

  const handleSearchAndFilter = (obj = {}, action = 'add') => {
    const search = obj.search !== undefined ? obj.search : localSearchString;
    delete obj.search;

    //Deleting filters if they are removed
    const filterValue = Object.values(obj)[0];
    if (!filterValue) {
      const columnId = Object.keys(obj)[0];
      delete filterByColumns[columnId];
      delete obj[columnId];
    }

    const filters = removeEmptyValues({
      ...(action === 'add' ? filterByColumns : {}),
      ...obj,
    });
    const columnIds = Object.keys(filters);

    setLocalSearchString(search);
    setFilterByColumns(filters);

    //Reset the filter if there is no search or filter
    if (!search.length && !columnIds.length) {
      updateRowData();
      return;
    }

    updateRowData((rows) =>
      rows?.filter((row) => {
        let rowMatch = false;

        if (search) {
          rowMatch = Object.keys(row).some((columnId) => {
            return doesRowMatch(row, columnId, [search]);
          });
        }

        if (columnIds?.length) {
          rowMatch = columnIds.every((columnId) => {
            const filterValue = filters[columnId];
            return doesRowMatch(row, columnId, [filterValue]);
          });
        }

        return rowMatch;
      })
    );
  };

  const getColumnLabel = (id) => {
    return columns.find((column) => column.id === id)?.label;
  };

  const displayedItemCountLabel = visibleRows?.length
    ? ` (displaying ${printRoundedNumber(visibleRows.length, 0)}${
        totalRowCount || allRows.length
          ? ` / ${printRoundedNumber(totalRowCount || allRows.length, 0)}`
          : ''
      } item${visibleRows?.length > 1 ? 's' : ''})`
    : '';

  const searchFromLabel = `Search from ${tableType}...${displayedItemCountLabel}`;

  const filterByColumnIds = Object.keys(filterByColumns);

  return (
    <GridContainer
      sx={{
        marginLeft: '-8px',
        width: 'calc(100% + 8px)',
      }}
      {...gridStyle}
    >
      {fetchRows || showSearch || renderAdditionalComponents ? (
        <GridItem xs={12}>
          <GridContainer sx={{ paddingX: '8px' }}>
            {fetchRows ? (
              <GridItem xs>
                <LabeledInput
                  type='text'
                  label={searchFromLabel}
                  placeholder={placeholder}
                  value={fetchQueries?.searchString}
                  sx={{
                    minHeight: '48px',
                  }}
                  onChange={(e) => {
                    const searchString = e.target.value;
                    // Do not make an expensive API call if the search string is the same.
                    if (searchString === fetchQueries?.searchString) {
                      return;
                    }
                    setFetchQueries?.((prev) => ({
                      ...prev,
                      searchString,
                    }));
                  }}
                />
              </GridItem>
            ) : showSearch ? (
              <GridItem xs>
                <Filter
                  fieldName={'search'}
                  initialValue={localSearchString || ''}
                  label={searchFromLabel}
                  placeholder={placeholder}
                  updateChange={handleSearchAndFilter}
                />
              </GridItem>
            ) : null}
            {renderAdditionalComponents ? (
              <GridItem xs={6}>
                {renderAdditionalComponents(allRows, fetchQueries)}
              </GridItem>
            ) : null}
          </GridContainer>
        </GridItem>
      ) : null}
      {filterByColumnIds?.length ? (
        <GridItem xs={12}>
          <Stack
            sx={{ paddingLeft: '8px' }}
            direction='row'
            alignItems='center'
            spacing={1}
          >
            {filterByColumnIds.map((key) => {
              return filterByColumns[key] ? (
                <Chip
                  key={key}
                  size='small'
                  label={
                    <Box
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant='caption'>
                        {getColumnLabel(key)}: {filterByColumns[key]}
                      </Typography>
                      <FaIcon
                        icon='close'
                        onClick={() => {
                          const filter = { ...filterByColumns };
                          delete filter[key];
                          handleSearchAndFilter(filter, 'delete');
                        }}
                      />
                    </Box>
                  }
                  sx={{ marginRight: '5px' }}
                />
              ) : null;
            })}
          </Stack>
        </GridItem>
      ) : null}
      <GridItem xs={12}>
        {showLoader ? (
          <div
            style={{
              height: style?.height || 400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Loader />
          </div>
        ) : useVirtualTable ? (
          <TableVirtuoso
            increaseViewportBy={{ top: 800, bottom: 300 }}
            style={{
              height: 400,
              overflow: 'auto',
              width: '100%',
              ...style,
            }}
            components={{
              Table: (props) => (
                <Table
                  {...props}
                  sx={{
                    width: '100%',
                    borderCollapse: 'collapse',
                  }}
                />
              ),
            }}
            data={virtualRows}
            itemContent={generateRow}
            fixedHeaderContent={fixedHeaderContent}
            fixedFooterContent={fixedFooterContent}
            endReached={(index) => {
              fetchRowsFromApi({
                page: Math.ceil((index + rowsPerPage / 2) / rowsPerPage),
                fetchCriteriaChanged: false,
              });
            }}
          />
        ) : (
          <Table
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              ...style,
            }}
          >
            {fixedHeaderContent()}
            {virtualRows.map((vr, i) => (
              <TableRow key={i}>{generateRow(i, vr)}</TableRow>
            ))}
            {fixedFooterContent()}
          </Table>
        )}
      </GridItem>
    </GridContainer>
  );
};

TableWrapper.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      isSortable: PropTypes.bool,
      width: PropTypes.number,
    })
  ).isRequired,
  transformRows: PropTypes.bool,
  style: PropTypes.object,
  expandRow: PropTypes.bool,
  orderBy: PropTypes.string,
  orderDirection: PropTypes.oneOf(['asc', 'desc']),
  showSearch: PropTypes.bool,
  placeholder: PropTypes.string,
  showFilter: PropTypes.bool,
  showLoader: PropTypes.bool,
  fetchRows: PropTypes.func,
  fetchQueries: PropTypes.object,
  setFetchQueries: PropTypes.func,
  filterVisibleRows: PropTypes.func,
  rowsPerPage: PropTypes.number,
  tableType: PropTypes.string,
  renderAdditionalComponents: PropTypes.func,
  gridStyle: PropTypes.object,
};

export default TableWrapper;
